/**
 * API client plugin
 */
import axios from 'axios';
import Vue from 'vue';
import RoutePlugin from './Routes';
import toastr from 'toastr';
import { displayApiValidationErrors } from '../helpers';
import {
  RESEARCH_ELECTRONIC_SIGNATURES_ERRORS,
  RESEARCH_MERGE_ERROS,
  RESEARCH_PIPELINE_ERRORS,
} from '@/config/research';

Vue.use(RoutePlugin);

const apiErrorHandler = (err) => {
  if (err.response) {
    switch (err.response.status) {
      case 500:
        console.error(`Server error: ${err.response.data?.message ?? 'Unknown'}`);
        break;
      case 404:
        console.error(`Server error: ${err.response.data?.message ?? 'Not Found'}`);
        break;
      case 401:
        if (err.response.data?.message?.includes(RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.INVALID_SIGNATURE)) {
          throw new Error(RESEARCH_ELECTRONIC_SIGNATURES_ERRORS.INVALID_SIGNATURE);
        }
        console.error(`Server error: ${err.response.data?.message ?? 'Not authorized'}`);
        break;
      case 403:
        // In case of group invitation error throw the error
        if (
        err.response.config?.url?.startsWith('/api/1.0/groups') &&
          err.response.config?.url?.endsWith('invitations')
      ) {
          throw err;
        }
        console.error(
          `Server error: ${err.response.data?.message ?? 'Logged in but access to requested area is forbidden'}`
        );
        break;
      case 400:
        console.error(`Server error: ${err.response.data?.message ?? 'Bad request'}`);
        if (err.response?.data?.message?.includes('Segmentation does not belong to subject')) {
          throw new Error('Segmentation does not belong to subject');
        }

        if (err.response?.data?.error?.includes(RESEARCH_MERGE_ERROS.NOT_SAME_PATIENT)) {
          throw new Error(RESEARCH_MERGE_ERROS.NOT_SAME_PATIENT);
        }

        if (err.response?.data?.error?.includes(RESEARCH_PIPELINE_ERRORS.EVENT_QC_IN_USE)) {
          throw new Error(RESEARCH_PIPELINE_ERRORS.EVENT_QC_IN_USE);
        }
        break;
      case 422:
        if (!err.response?.data?.errors) throw err;
        let errors = JSON.parse(JSON.stringify(err.response.data.errors));
        if (errors) {
          displayApiValidationErrors(errors);
        } else {
          toastr.error('Data validation failed');
        }
        break;
    }
  } else {
    if (!axios.isCancel(err)) {
      console.error('API request failed.');
    }
  }
};

const ApiPlugin = {
  install: (Vue, options) => {
    Vue.prototype.$api = function (routeName, routeParams = {}, requestBody = {}, cancelToken = null, signal = null) {
      let params = routeParams;

      if (!Array.isArray(routeParams)) {
        params = { version: '1.0', ...routeParams };
      }

      const route = this.$route(routeName, params);
      const method = route.ziggy.namedRoutes[routeName].methods[0].toLowerCase();
      const options = { method, url: route.url(), data: requestBody, cancelToken, signal };
      return axios.request(options).catch(apiErrorHandler);
    };

    Vue.prototype.$pollApiUntil = function (
      routeName,
      routeParams,
      requestBody,
      condition,
      pollTimeout = 1000,
      cancelToken = null,
      signal = null
    ) {
      return new Promise((resolve, reject) => {
        const poll = () => {
          this.$api(routeName, routeParams, requestBody, cancelToken, signal)
            .then((response) => {
              if (condition(response)) {
                resolve(response);
              } else {
                setTimeout(poll, 1000);
              }
            })
            .catch((error) => reject(error));
        };

        poll();
      });
    };
  },
};

let cancelTokens = {};

export const setCancelToken = (id) => {
  return (cancelTokens[id] = axios.CancelToken.source());
};

export const getCancelToken = (id, force = false) => {
  if (force) {
    setCancelToken(id);
  }

  return (cancelTokens[id] ??= axios.CancelToken.source());
};

export const setCancelTokenIfNonExistent = (id) => {
  return getCancelToken(id) ?? setCancelToken(id);
};

export default ApiPlugin;
